import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Globe, Users, Share2, Copy, Check, ChevronRight } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { fetchAllProfiles } from "@/lib/api";

function CopyBtn({ text, dark }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className={`p-1.5 rounded-md flex-shrink-0 ${dark ? 'hover:bg-white/20' : 'hover:bg-[#0B2A47]/10'}`}>
      {copied ? <Check className={`w-4 h-4 ${dark ? 'text-[#8FC4E8]' : 'text-[#1E7BC4]'}`} /> : <Copy className={`w-4 h-4 ${dark ? 'text-white/70' : 'text-[#0B2A47]/50'}`} />}
    </button>
  );
}

export default function MembersDirectory() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const profiles = await fetchAllProfiles();
        setMembers(profiles.sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        setMembers([]);
      } finally {
        setLoading(false);
      }
    }
    load();
    document.title = "Our Members | CHART Networking";
  }, []);

  const directoryUrl = `${window.location.origin}/members`;
  const colors = [
    "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]",
    "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]", "bg-[#1E7BC4]"
  ];

  function handleShare() {
    navigator.clipboard.writeText(directoryUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      window.prompt("Copy this link:", directoryUrl);
    });
  }

  return (
    <div className="min-h-screen bg-[#F5F2EC]">
      {/* Header */}
      <div className="bg-[#1E7BC4] text-white sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#1E7BC4] bg-white hover:bg-[#F5F2EC] rounded-lg transition-colors">
            CHART Home
          </Link>
          <button onClick={handleShare} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
            {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Share2 className="w-3.5 h-3.5" /> Share</>}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-8">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-full bg-[#1E7BC4]/10 flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-[#1E7BC4]" />
          </div>
          <h1 className="font-heading text-2xl font-extrabold text-[#0B2A47] mb-2">Our Members</h1>
          <p className="text-[#5A7080] text-sm">Tap any member to see their full profile, business cards, and contact info</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-4 border-[#1E7BC4] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((m, i) => {
              const businesses = m.businesses || [];
              const hasBiz = businesses.length > 0;
              return (
                <Link
                  key={m.name}
                  to={`/member/${encodeURIComponent(m.name)}`}
                  className="block bg-white border border-[#E5E0D8] rounded-xl p-4 hover:border-[#1E7BC4]/40 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start gap-3.5">
                    {m.headshot ? (
                      <img src={m.headshot} alt={m.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-[#E5E0D8]" />
                    ) : (
                      <div className={`w-12 h-12 rounded-full ${colors[i % colors.length]} text-white font-extrabold text-xl flex items-center justify-center flex-shrink-0`}>
                        {m.name[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-[15px] font-bold text-[#0B2A47]">{m.name}</p>
                        <ChevronRight className="w-4 h-4 text-[#C5D0DA] group-hover:text-[#1E7BC4] transition-colors ml-auto flex-shrink-0" />
                      </div>
                      {hasBiz ? (
                        businesses.map((biz, bi) => (
                          <div key={bi} className={bi > 0 ? "mt-2.5 pt-2 border-t border-[#F0EDE8]" : ""}>
                            <p className="text-xs font-bold text-[#1E7BC4]">{biz.name}</p>
                            {biz.title && <p className="text-[11px] text-[#5A7080]">{biz.title}</p>}
                            {biz.description && <p className="text-[11px] text-[#8BA0B0] mt-0.5 leading-relaxed">{biz.description}</p>}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5">
                              {biz.phone && <span className="inline-flex items-center gap-1 text-[11px] text-[#3D5466]"><Phone className="w-3 h-3 text-[#8BA0B0]" /> {biz.phone}</span>}
                              {biz.email && <span className="inline-flex items-center gap-1 text-[11px] text-[#3D5466]"><Mail className="w-3 h-3 text-[#8BA0B0]" /> {biz.email}</span>}
                              {biz.website && <span className="inline-flex items-center gap-1 text-[11px] text-[#3D5466]"><Globe className="w-3 h-3 text-[#8BA0B0]" /> {biz.website}</span>}
                            </div>
                          </div>
                        ))
                      ) : (
                        <>
                          {m.businessName && <p className="text-xs font-bold text-[#1E7BC4]">{m.businessName}</p>}
                          {m.title && <p className="text-[11px] text-[#5A7080]">{m.title}</p>}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                            {m.phone && <span className="inline-flex items-center gap-1 text-[11px] text-[#3D5466]"><Phone className="w-3 h-3 text-[#8BA0B0]" /> {m.phone}</span>}
                            {m.email && <span className="inline-flex items-center gap-1 text-[11px] text-[#3D5466]"><Mail className="w-3 h-3 text-[#8BA0B0]" /> {m.email}</span>}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Share footer */}
      <div className="bg-[#0B2A47] text-white">
        <div className="max-w-3xl mx-auto px-5 py-10 text-center">
          <h3 className="font-heading text-lg font-extrabold mb-1">Share Our Directory</h3>
          <p className="text-[#6888A5] text-sm mb-5">Know someone who should connect with us?</p>

          <div className="inline-block bg-white p-4 rounded-xl mb-5">
            <QRCodeSVG value={directoryUrl} size={140} level="M" bgColor="#ffffff" fgColor="#0B2A47" />
          </div>

          <div className="flex items-center gap-2 max-w-sm mx-auto bg-white/10 border border-white/20 rounded-lg px-3 py-2.5 mb-5">
            <Globe className="w-4 h-4 text-white/50 flex-shrink-0" />
            <a href="/members" className="text-sm font-mono text-white/80 truncate flex-1 hover:text-white underline underline-offset-2">{directoryUrl}</a>
            <CopyBtn text={directoryUrl} dark />
          </div>

          <button onClick={handleShare} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-[#1E7BC4] hover:bg-[#1769A8] rounded-xl transition-colors">
            {copied ? <><Check className="w-4 h-4" /> Link Copied!</> : <><Share2 className="w-4 h-4" /> Share Directory</>}
          </button>
        </div>
      </div>
    </div>
  );
}
